import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { ListColumnDirective } from "@components/list/list-column.directive";
import { ListComponent } from "@components/list/list.component";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import _ from "lodash";
import { firstValueFrom } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ToastService } from "~app/common/components/toast/toast.service";
import { DestineringsalternativDTO } from "~app/common/models/planera/v2/DestineringsalternativDTO";
import { CommandService } from "~app/common/services/cqs/command.service";
import { EventHubService } from "~app/common/services/eventhub.service";
import { StringUtils } from "~app/common/utils/string-utils";
import { AddArbetsorderIntressentCommand } from "../../commands/AddArbetsorderIntressentCommand";
import { AddManuelltDestineringsalternativCommand } from "../../commands/AddManuelltDestineringsalternativCommand";
import { DeleteManuelltDestineringsalternativCommand } from "../../commands/DeleteManuelltDestineringsalternativCommand";
import { ArbetsorderService } from "../../services/arbetsorder.service";
import { DestineringsalternativManuella } from "../arbetsorder-destineringsalternativ.models";

export interface ArbetsorderDestineringsalternativManuellaData {
    arbetsorder: IDestineringsalternativManuellaItemArbetsorder;
    checkAddArbetsorderIntressent: boolean;
    valdaManuellaRader: IDestineringsalternativManuellaItemRad[];
}

export interface IDestineringsalternativManuellaItemArbetsorder {
    id: number;
}

export interface IDestineringsalternativManuellaItemRad {
    foradlatHandelssortimentId: number;
    handelssortimentId: number;
    mottagningsplatsId: number;
}

@Component({
    selector: "rask-arbetsorder-destineringsalternativ-manuella",
    templateUrl: "./arbetsorder-destineringsalternativ-manuella.component.html",
    styleUrls: ["./arbetsorder-destineringsalternativ-manuella.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ListComponent,
        ListColumnDirective,
        CheckboxComponent,
        FormsModule,
        ButtonComponent
    ]
})

export class ArbetsorderDestineringsalternativManuellaComponent implements OnInit {
    alternativaHandelssortimentlist: DestineringsalternativManuella[];
    data: ArbetsorderDestineringsalternativManuellaData;
    kundorderradHandelssortimentlist: DestineringsalternativManuella[];
    modal = inject(NgbActiveModal);

    private readonly arbetsorderService = inject(ArbetsorderService);
    private readonly busyService = inject(BusyService);
    private readonly commandService = inject(CommandService);
    private readonly eventHubService = inject(EventHubService);
    private readonly toastService = inject(ToastService);

    canSave() {
        const tmpAlladestineringsAlternativManuella = [
            ...(this.alternativaHandelssortimentlist || []),
            ...(this.kundorderradHandelssortimentlist || [])
        ];
        return tmpAlladestineringsAlternativManuella?.some(x => x.isAdd || x.isRemoved);
    }

    dismiss() {
        this.modal.close();
    }

    ngOnInit(): void {
        this.initList();
    }

    save() {
        const tmpAlladestineringsAlternativManuella = [
            ...this.alternativaHandelssortimentlist,
            ...this.kundorderradHandelssortimentlist
        ];
        tmpAlladestineringsAlternativManuella.forEach(x => {
            if (x.isAdd) {
                const command = new AddManuelltDestineringsalternativCommand(
                    this.data.arbetsorder.id,
                    x.handelssortimentId,
                    x.mottagningsplatsId,
                    x.destineringsursprung,
                    x.egenskapskodX,
                    x.overstyrdAvlastningsplats,
                    x.foradlatHandelssortimentId,
                    x.inmatningshandelssortimentId);
                this.commandService.addTransactionCommand(command);
            } else if (x.isRemoved) {
                const command = new DeleteManuelltDestineringsalternativCommand(
                    this.data.arbetsorder.id,
                    x.handelssortimentId,
                    x.mottagningsplatsId,
                    x.destineringsursprung,
                    x.foradlatHandelssortimentId);
                this.commandService.addTransactionCommand(command);
            }
        });

        if (this.data.checkAddArbetsorderIntressent) {
            this.eventHubService.registerListener(`arbetsorder/${this.data.arbetsorder.id.toString()}`);

            const intressentCommand = new AddArbetsorderIntressentCommand((instance) => {
                instance.arbetsorderId = this.data.arbetsorder.id;
            });
            this.commandService.addTransactionCommand(intressentCommand);
        }

        const worker = this.busyService.addWork(this.commandService.pushCommandQueueTransaction());
        worker.subscribe(result => {
            this.toastService.add("Ändringar sparade");
            this.modal.close(true);
        });
    }

    writeMottagningsplats(alternativ: DestineringsalternativDTO) {
        if (alternativ.foradlatHandelssortimentId == null) {
            return alternativ.mottagningsplatsNamn;
        }

        return StringUtils.suffixString(alternativ.mottagningsplatsNamn, "//FLIS", 20);
    }

    private initList(): void {
        firstValueFrom(this.arbetsorderService.getDestineringsalternativManuella(this.data.arbetsorder.id)).then(x => {
            const tmpDestineringsAlternativManuella = [];
            x.destineringalternativManuella.forEach(d => {
                const destineringsAlternativ = new DestineringsalternativManuella(dm => {
                    dm.mottagningsplatsNamn = this.writeMottagningsplats(d);
                    dm.handelssortimentNamn = d.handelssortimentNamn;
                    dm.handelssortimentId = d.handelssortimentId;
                    dm.mottagningsplatsnummer = d.mottagningsplatsnummer;
                    dm.mottagningsplatsId = d.mottagningsplatsId;
                    dm.laggtill = this.isVald(d);
                    dm.tidigareVald = this.isVald(d);
                    dm.destineringsursprung = d.destineringsursprung;
                    dm.egenskapskodX = d.egenskapskodX;
                    dm.overstyrdAvlastningsplats = d.overstyrdAvlastningsplats;
                    dm.foradlatHandelssortimentId = d.foradlatHandelssortimentId;
                    dm.inmatningshandelssortimentId = d.inmatningshandelssortimentId;
                });
                tmpDestineringsAlternativManuella.push(destineringsAlternativ);
            });

            const sortedDestineringsalternativ = _.orderBy<DestineringsalternativManuella>(tmpDestineringsAlternativManuella, m => m.handelssortimentId);

            const setHeaderRow = (list: DestineringsalternativManuella[]): DestineringsalternativManuella[] => {
                list.forEach(listItem => {
                    // eslint-disable-next-line sonarjs/no-nested-functions
                    if (!list.some(y => y.headerRow === listItem.handelssortimentNamn)) {
                        listItem.headerRow = listItem.handelssortimentNamn;
                    }
                });
                return list;
            };
            this.alternativaHandelssortimentlist = setHeaderRow(sortedDestineringsalternativ.filter(y => y.isAlternativt) ?? []);
            this.kundorderradHandelssortimentlist = setHeaderRow(sortedDestineringsalternativ.filter(y => !y.isAlternativt) ?? []);
        });
    }

    private isVald(d: DestineringsalternativDTO): boolean {
        return this.data.valdaManuellaRader.some(z => z.mottagningsplatsId === d.mottagningsplatsId &&
            z.handelssortimentId === d.handelssortimentId &&
            z.foradlatHandelssortimentId === d.foradlatHandelssortimentId);
    }
}
